import { useCallback, useEffect, useRef, useState } from "react";

/** Floors, so a panel can never be dragged down to nothing. */
const MIN_WIDTH_PX = 300;
const MIN_HEIGHT_PX = 240;
/** Gap the box keeps from every viewport edge. */
const MARGIN_PX = 12;
/** Keyboard nudge per arrow press. */
const STEP_PX = 24;
/** Elements whose own click must never start a move of their container. */
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [contenteditable="true"]';

const clamp = (value, min, max) =>
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Position and size of a floating panel, as ONE piece of state.
 *
 * They were previously two independent hooks — one for the position, one for
 * the size — and they fought: growing the box downwards pushed it past the
 * viewport, so the position hook clamped it back up, which pinned the bottom
 * edge to the screen and made vertical resizing look like it did nothing. An
 * edge can only be dragged correctly by something that knows both where the box
 * is and how big it is, because resizing the top or left edge has to move the
 * box and change its size in the same frame.
 *
 * Every edge behaves the way a window manager's does: the edge you hold follows
 * the pointer and the opposite edge stays put. Growth stops when the held edge
 * reaches the screen, rather than the box sliding away from you.
 *
 * @param {Object} opts
 * @param {string} opts.anchorKey - Shared placement key. The launcher reads and
 *   writes the same one, so the two open in the same place.
 * @param {string} opts.sizeKey - Prefix for the persisted width and height.
 * @param {number} opts.defaultWidth
 * @param {number} opts.defaultHeight
 * @param {number} opts.maxWidth
 * @param {(box: {left: number, top: number, width: number, height: number}) => {left: number, top: number}} opts.toAnchor
 * @param {(anchor: {left: number, top: number}, width: number, height: number) => {left: number, top: number}} opts.fromAnchor
 * @param {boolean} [opts.disabled=false] - Expanded/mobile: nothing to drag.
 * @returns {{left: number, top: number, width: number, height: number,
 *   isBusy: boolean, moveHandleProps: Object,
 *   resizeHandleProps: (edge: string) => Object}}
 */
export function useFloatingBox({
  anchorKey,
  sizeKey,
  defaultWidth,
  defaultHeight,
  maxWidth,
  toAnchor,
  fromAnchor,
  disabled = false,
}) {
  const widthKey = `${sizeKey}Width`;
  const heightKey = `${sizeKey}Height`;

  const [box, setBox] = useState(() => {
    const readPx = (key, fallback) => {
      try {
        const raw = Number(localStorage.getItem(key));
        return Number.isFinite(raw) && raw > 0 ? raw : fallback;
      } catch {
        return fallback;
      }
    };
    const width = clamp(
      readPx(widthKey, defaultWidth),
      MIN_WIDTH_PX,
      Math.min(maxWidth, window.innerWidth - MARGIN_PX * 2)
    );
    const height = clamp(
      readPx(heightKey, defaultHeight),
      MIN_HEIGHT_PX,
      window.innerHeight - MARGIN_PX * 2
    );

    let placement = null;
    try {
      const raw = localStorage.getItem(anchorKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Number.isFinite(parsed?.left) && Number.isFinite(parsed?.top)) {
          placement = fromAnchor(parsed, width, height);
        }
      }
    } catch {
      placement = null;
    }
    const fallbackPlacement = {
      left: window.innerWidth - width - 20,
      top: window.innerHeight - height - 20,
    };
    const { left, top } = placement || fallbackPlacement;

    return {
      width,
      height,
      left: clamp(left, MARGIN_PX, window.innerWidth - width - MARGIN_PX),
      top: clamp(top, MARGIN_PX, window.innerHeight - height - MARGIN_PX),
    };
  });

  const [isBusy, setIsBusy] = useState(false);
  // The box as it was when the gesture began, plus which edge is held.
  const originRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(widthKey, String(Math.round(box.width)));
      localStorage.setItem(heightKey, String(Math.round(box.height)));
      localStorage.setItem(anchorKey, JSON.stringify(toAnchor(box)));
    } catch {
      // Persistence is a nicety; a full or blocked store must not break drag.
    }
  }, [widthKey, heightKey, anchorKey, box, toAnchor]);

  // Only on a real viewport change — never as a reaction to the box's own size,
  // which is what created the fight this hook exists to end.
  useEffect(() => {
    if (disabled) return undefined;
    const onResize = () =>
      setBox((prev) => {
        const width = clamp(
          prev.width,
          MIN_WIDTH_PX,
          Math.max(MIN_WIDTH_PX, window.innerWidth - MARGIN_PX * 2)
        );
        const height = clamp(
          prev.height,
          MIN_HEIGHT_PX,
          Math.max(MIN_HEIGHT_PX, window.innerHeight - MARGIN_PX * 2)
        );
        const next = {
          width,
          height,
          left: clamp(
            prev.left,
            MARGIN_PX,
            Math.max(MARGIN_PX, window.innerWidth - width - MARGIN_PX)
          ),
          top: clamp(
            prev.top,
            MARGIN_PX,
            Math.max(MARGIN_PX, window.innerHeight - height - MARGIN_PX)
          ),
        };
        const same =
          next.width === prev.width &&
          next.height === prev.height &&
          next.left === prev.left &&
          next.top === prev.top;
        return same ? prev : next;
      });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [disabled]);

  /**
   * Apply a pointer delta to the box the gesture started from.
   *
   * Growth spills to the opposite side. The edge you hold normally moves while
   * the far edge stays put — but once the held edge reaches the screen, the box
   * keeps growing by pushing the FAR edge outwards instead of stopping. Drag
   * the bottom of a panel parked at the bottom of the screen and it grows
   * upwards; drag the right edge of one parked against the right and it grows
   * leftwards. Both axes work the same way.
   *
   * Without this, a panel resting near an edge simply refused to get bigger in
   * that direction, which read as the resize being broken.
   */
  const applyGesture = useCallback(
    (origin, dx, dy) => {
      const { box: o, edge } = origin;

      if (edge === "move") {
        return {
          ...o,
          left: clamp(
            o.left + dx,
            MARGIN_PX,
            window.innerWidth - o.width - MARGIN_PX
          ),
          top: clamp(
            o.top + dy,
            MARGIN_PX,
            window.innerHeight - o.height - MARGIN_PX
          ),
        };
      }

      // Ceilings are the whole viewport, not just the room on the held side —
      // that is what lets the growth spill past a blocked edge.
      const widthCeiling = Math.min(
        maxWidth,
        Math.max(MIN_WIDTH_PX, window.innerWidth - MARGIN_PX * 2)
      );
      const heightCeiling = Math.max(
        MIN_HEIGHT_PX,
        window.innerHeight - MARGIN_PX * 2
      );

      let { left, top, width, height } = o;

      if (edge.includes("e") || edge.includes("w")) {
        const grewFromLeftEdge = edge.includes("w");
        width = clamp(
          grewFromLeftEdge ? o.width - dx : o.width + dx,
          MIN_WIDTH_PX,
          widthCeiling
        );
        // Hold the far edge still where there is room; slide the box back on
        // screen where there isn't.
        const anchored = grewFromLeftEdge ? o.left + (o.width - width) : o.left;
        left = clamp(
          anchored,
          MARGIN_PX,
          Math.max(MARGIN_PX, window.innerWidth - width - MARGIN_PX)
        );
      }

      if (edge.includes("n") || edge.includes("s")) {
        const grewFromTopEdge = edge.includes("n");
        height = clamp(
          grewFromTopEdge ? o.height - dy : o.height + dy,
          MIN_HEIGHT_PX,
          heightCeiling
        );
        const anchored = grewFromTopEdge ? o.top + (o.height - height) : o.top;
        top = clamp(
          anchored,
          MARGIN_PX,
          Math.max(MARGIN_PX, window.innerHeight - height - MARGIN_PX)
        );
      }

      return { left, top, width, height };
    },
    [maxWidth]
  );

  const endGesture = useCallback((event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    originRef.current = null;
    setIsBusy(false);
  }, []);

  const gestureProps = useCallback(
    (edge) => ({
      onPointerDown: (event) => {
        if (event.button !== 0) return;
        // A press on a control inside the handle belongs to that control.
        if (edge === "move" && event.target?.closest?.(INTERACTIVE_SELECTOR)) {
          return;
        }
        event.currentTarget.setPointerCapture?.(event.pointerId);
        originRef.current = {
          x: event.clientX,
          y: event.clientY,
          box,
          edge,
        };
        setIsBusy(true);
        event.preventDefault();
      },
      onPointerMove: (event) => {
        const origin = originRef.current;
        if (!origin) return;
        setBox(
          applyGesture(
            origin,
            event.clientX - origin.x,
            event.clientY - origin.y
          )
        );
      },
      onPointerUp: endGesture,
      onPointerCancel: endGesture,
    }),
    [box, applyGesture, endGesture]
  );

  const nudge = useCallback(
    (edge, dx, dy) =>
      setBox((prev) => applyGesture({ box: prev, edge }, dx, dy)),
    [applyGesture]
  );

  const moveHandleProps = disabled
    ? {}
    : {
        ...gestureProps("move"),
        onKeyDown: (event) => {
          if (event.key === "ArrowUp") nudge("move", 0, -STEP_PX);
          else if (event.key === "ArrowDown") nudge("move", 0, STEP_PX);
          else if (event.key === "ArrowLeft") nudge("move", -STEP_PX, 0);
          else if (event.key === "ArrowRight") nudge("move", STEP_PX, 0);
          else return;
          event.preventDefault();
        },
      };

  /**
   * Props for one resize handle. `edge` is a compass direction: 'n', 's', 'e',
   * 'w' or a corner such as 'se'.
   */
  const resizeHandleProps = useCallback(
    (edge) => {
      if (disabled) return {};
      const vertical = edge.includes("n") || edge.includes("s");
      const horizontal = edge.includes("e") || edge.includes("w");
      return {
        ...gestureProps(edge),
        role: "separator",
        "aria-orientation": horizontal ? "vertical" : "horizontal",
        "aria-label": `Resize panel ${edge}`,
        tabIndex: 0,
        title: "Drag to resize",
        onKeyDown: (event) => {
          if (horizontal && event.key === "ArrowRight") nudge(edge, STEP_PX, 0);
          else if (horizontal && event.key === "ArrowLeft") nudge(edge, -STEP_PX, 0);
          else if (vertical && event.key === "ArrowDown") nudge(edge, 0, STEP_PX);
          else if (vertical && event.key === "ArrowUp") nudge(edge, 0, -STEP_PX);
          else return;
          event.preventDefault();
        },
      };
    },
    [disabled, gestureProps, nudge]
  );

  return { ...box, isBusy, moveHandleProps, resizeHandleProps };
}

export default useFloatingBox;
